import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import pharmacyVisibilityService, {
  type PharmacyVisibilityEntry,
  type PharmacyVisibilityRecord,
  type PharmacyVisibilityResponse,
} from '../../services/pharmacyVisibilityService';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { useAuth } from '../useAuth';
import {
  DELIVERY_TYPE_CHOICE_VISIBILITY_KEY,
  PATHWAY_CHOICE_VISIBILITY_KEY,
  VISIBILITY_CATALOGUE,
  type VisibilityContext,
} from '../../visibility/visibilityCatalogue';

export const pharmacyVisibilityQueryKey = (pharmacyId?: string | null) =>
  ['pharmacy-visibility', pharmacyId] as const;

/**
 * Pharmacy-level UI visibility record (System Configuration -> Visibility).
 * One query definition for every consumer, keyed by pharmacy — mirrors
 * usePharmacyFeatures.
 */
export function usePharmacyVisibility(pharmacyId?: string | null) {
  return useQuery<PharmacyVisibilityResponse>({
    queryKey: pharmacyVisibilityQueryKey(pharmacyId),
    queryFn: () => pharmacyVisibilityService.get(pharmacyId as string),
    enabled: !!pharmacyId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The resolver's own names for the GET response's `blocks` shape — same wire
 * shape as `PharmacyVisibilityEntry`/`PharmacyVisibilityRecord`
 * (services/pharmacyVisibilityService.ts), one definition, re-exported here
 * under the names the rest of the visibility feature imports.
 */
export type VisibilityRecordEntry = PharmacyVisibilityEntry;
export type VisibilityRecord = PharmacyVisibilityRecord;

/**
 * R2 — the one resolver, pure. Absent/unset key ⇒ the catalogue default (an
 * unknown key is never hidden, R1); a null/undefined role is never hidden by
 * role (D3, no-lockout). Every gate in the app calls this through
 * `useBlockHidden()`/`useBlocksHidden()` below — no page reads
 * `pharmacy.visibility` directly.
 */
export function resolveBlockHidden(
  key: string,
  record: VisibilityRecord | undefined,
  role: string | null | undefined,
  ctx: VisibilityContext,
): boolean {
  const block = VISIBILITY_CATALOGUE.find((b) => b.key === key);
  if (!block) return false; // unknown key: never hide (R1)
  const entry = record?.[key];
  if (!entry || !entry.isSet) {
    return typeof block.defaultHidden === 'function' ? block.defaultHidden(ctx) : block.defaultHidden;
  }
  if (entry.hidden) return true;
  return !!role && entry.hiddenFor.includes(role);
}

/**
 * The role a `hiddenFor` list is matched against. An INACTIVE role assignment
 * is no role at all — `useAuth().hasCustomRole()` already requires
 * `isActive === true`, so a deactivated assignment must not hide anything by
 * role here either (D3, no-lockout).
 */
export function activeRoleOf(
  customRole: { customRole: string; isActive: boolean } | null | undefined,
): string | null {
  return customRole?.isActive ? customRole.customRole : null;
}

/**
 * R3: while the visibility query is loading, `data` is undefined, so the
 * resolver above falls straight through to the catalogue default — no extra
 * loading state to thread through every gate.
 */
export function useBlockHidden(key: string, pharmacyId?: string | null): boolean {
  const { record, ctx } = usePharmacyVisibilityInputs(pharmacyId);
  const { customRole } = useAuth();
  return resolveBlockHidden(key, record, activeRoleOf(customRole), ctx);
}

/**
 * PRX-195: whether the pharmacy has no Specialty pathway (PharmAssist), so the
 * "Which pathway is this?" question is skipped and the pathway is Digital.
 * Pharmacy-wide: resolved with NO role, so hiding the block for one role
 * never splits the clinical workflow by role.
 */
export function usePharmacyHasNoSpecialtyPathway(pharmacyId?: string | null): boolean {
  const { record, ctx } = usePharmacyVisibilityInputs(pharmacyId);
  return resolveBlockHidden(PATHWAY_CHOICE_VISIBILITY_KEY, record, null, ctx);
}

/**
 * PRX-211 Q9: whether the pharmacy has no infusion clinic (PharmAssist), so
 * the new start wizard skips its Delivery Type step and the subscription is
 * Patient home delivery. Pharmacy-wide like the pathway question above:
 * resolved with NO role.
 */
export function usePharmacyHidesDeliveryTypeChoice(pharmacyId?: string | null): boolean {
  const { record, ctx } = usePharmacyVisibilityInputs(pharmacyId);
  return resolveBlockHidden(DELIVERY_TYPE_CHOICE_VISIBILITY_KEY, record, null, ctx);
}

/** The visibility record and PMS context for the pharmacy a gate works on. */
function usePharmacyVisibilityInputs(pharmacyId?: string | null): {
  record: VisibilityRecord | undefined;
  ctx: VisibilityContext;
} {
  const activePharmacy = usePharmacyStore((s) => s.activePharmacy);
  const pharmacies = usePharmacyStore((s) => s.pharmacies);
  // PRX-122: a page working on a record of a given pharmacy (a billing item)
  // passes it, so the PMS type and the record come from THAT pharmacy. One the
  // store doesn't know has an unknown PMS type: the catalogue default decides.
  const pharmacy =
    !pharmacyId || pharmacyId === activePharmacy?.id
      ? activePharmacy
      : pharmacies.find((p) => p.id === pharmacyId);
  const { data } = usePharmacyVisibility(pharmacyId || activePharmacy?.id);
  return { record: data?.blocks, ctx: { databaseType: pharmacy?.databaseType ?? null } };
}

/**
 * Resolves several blocks off ONE `usePharmacyVisibility()` subscription
 * instead of one per key (e.g. the five top-bar quick actions in a single
 * call) — same loading behaviour as `useBlockHidden` (R3).
 */
export function useBlocksHidden(keys: string[]): Record<string, boolean> {
  const activePharmacy = usePharmacyStore((s) => s.activePharmacy);
  const { customRole } = useAuth();
  const { data } = usePharmacyVisibility(activePharmacy?.id);
  const role = activeRoleOf(customRole);
  const databaseType = activePharmacy?.databaseType ?? null;
  // Callers pass a fresh array literal on every render (the five top-bar
  // actions, say), so memoise on the KEYS themselves rather than on the array
  // identity — otherwise this returns a new object every render and every
  // consumer re-renders with it.
  const keyList = keys.join('|');

  return useMemo(() => {
    const ctx: VisibilityContext = { databaseType };
    return keyList
      .split('|')
      .filter((key) => key.length > 0)
      .reduce<Record<string, boolean>>((acc, key) => {
        acc[key] = resolveBlockHidden(key, data?.blocks, role, ctx);
        return acc;
      }, {});
  }, [keyList, data, role, databaseType]);
}
